"use client"

import type React from "react"

import { useState, useCallback } from "react"
import { useDropzone } from "react-dropzone"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Upload, FileText, X, CheckCircle, AlertCircle, Brain, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { useTranslation } from "react-i18next"

interface FileProcessingState {
  file: File
  status: 'uploading' | 'extracting' | 'completed' | 'error'
  analysisStatus?: 'pending' | 'analyzing' | 'analyzed' | 'analysis_error'
  extractedText?: string
  cvUrl?: string
  errorMessage?: string
  analysisErrorMessage?: string
  progress: number
}

interface DragDropUploadProps {
  onFilesUploaded?: (files: File[]) => void
  onFilesSelected?: (files: File[]) => void
  onFileRemoved?: (files: File[]) => void
  accept?: Record<string, string[]>
  maxFiles?: number
  maxSize?: number
  disabled?: boolean
  showPreview?: boolean
  fileProcessingStates?: FileProcessingState[]
  children?: React.ReactNode
  className?: string
}

export function DragDropUpload({
  onFilesUploaded,
  onFilesSelected,
  onFileRemoved,
  accept = {
    "application/pdf": [".pdf"],
    "application/msword": [".doc"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
  },
  maxFiles = 1,
  maxSize = 5 * 1024 * 1024, // 5MB
  disabled = false,
  showPreview = true,
  fileProcessingStates = [],
  children,
  className,
}: DragDropUploadProps) {
  const { t } = useTranslation("evaluate-cv")
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [errors, setErrors] = useState<string[]>([])

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  const validateFile = (file: File): string | null => {
    if (file.size > maxSize) {
      return `File "${file.name}" is too large. Maximum size is ${formatFileSize(maxSize)}.`
    }

    const acceptedTypes = Object.keys(accept)
    const acceptedExtensions = Object.values(accept).flat()

    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase()
    const isValidType =
      acceptedTypes.some((type) => file.type.includes(type.split("/")[0])) || acceptedExtensions.includes(fileExtension)

    if (!isValidType) {
      return `File "${file.name}" is not a supported format. Accepted formats: ${acceptedExtensions.join(", ")}`
    }

    return null
  }



  const handleFiles = useCallback(
    async (files: File[]) => {
      if (disabled) return

      setErrors([])
      const newErrors: string[] = []

      // If maxFiles is 1 and we already have a file, and a new file is dropped,
      // we replace the existing file instead of showing an error.
      if (maxFiles === 1 && files.length > 0) {
        const file = files[0]
        const error = validateFile(file)
        if (error) {
          setErrors([error])
          return
        }

        const newUploadedFiles = [file]
        setUploadedFiles(newUploadedFiles)
        onFilesUploaded?.(newUploadedFiles)
        onFilesSelected?.(newUploadedFiles)
        return
      }

      // Existing validation for multiple files
      if (uploadedFiles.length + files.length > maxFiles) {
        newErrors.push(`Maximum ${maxFiles} file(s) allowed.`)
      }

      // Validate each file
      const validFiles: File[] = []
      for (const file of files) {
        const error = validateFile(file)
        if (error) {
          newErrors.push(error)
        } else {
          validFiles.push(file)
        }
      }

      if (newErrors.length > 0) {
        setErrors(newErrors)
        return
      }

      const newUploadedFiles = [...uploadedFiles, ...validFiles].slice(0, maxFiles)
      setUploadedFiles(newUploadedFiles)

      // Call both callback functions for backward compatibility
      onFilesUploaded?.(newUploadedFiles)
      onFilesSelected?.(newUploadedFiles)
    },
    [uploadedFiles, maxFiles, maxSize, disabled, onFilesUploaded, onFilesSelected],
  )

  const removeFile = (fileToRemove: File) => {
    const newFiles = uploadedFiles.filter((file) => file !== fileToRemove)
    setUploadedFiles(newFiles)

    // Call file removed callback, fallback to files uploaded for backward compatibility
    if (onFileRemoved) {
      onFileRemoved(newFiles)
    } else {
      onFilesUploaded?.(newFiles)
      onFilesSelected?.(newFiles)
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFiles,
    accept,
    maxFiles,
    maxSize,
    disabled,
    multiple: maxFiles > 1,
  })

  return (
    <div className={cn("space-y-4", className)}>
      {/* Upload Area */}
      <div
        {...getRootProps()}
        className={cn(
          "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
          isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400",
          disabled && "opacity-50 cursor-not-allowed",
          maxFiles > 1 && uploadedFiles.length >= maxFiles && "opacity-50 cursor-not-allowed",
        )}
      >
        <input {...getInputProps()} />

        {children ? (
          children
        ) : (
          <div>
            <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-700">
              {isDragActive ? t("dropFilesHere") : t("clickToUpload")}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {Object.values(accept).flat().join(", ")} (max {formatFileSize(maxSize)})
            </p>
            {maxFiles > 1 && <p className="text-xs text-gray-500">{t("upToFilesAllowed", { count: maxFiles })}</p>}
          </div>
        )}
      </div>

      {/* Error Messages */}
      {errors.length > 0 && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            <ul className="list-disc list-inside space-y-1">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* File Preview */}
      {showPreview && uploadedFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">{t("selectedFiles")}</h4>
          {uploadedFiles.map((file) => {
            const processingState = fileProcessingStates.find(state => state.file.name === file.name)
            return (
              <div key={file.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3 flex-1">
                  <div className="flex-shrink-0">
                    {processingState?.status === 'completed' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : processingState?.status === 'error' ? (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    ) : processingState?.status === 'uploading' || processingState?.status === 'extracting' ? (
                      <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <FileText className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                    {processingState && (
                      <div className="flex items-center space-x-2 text-xs text-gray-600 mt-1">
                        {/* Extraction Status */}
                        <span>
                          {processingState.status === 'uploading' && t("uploading")}
                          {processingState.status === 'extracting' && t("extractingText")}
                          {processingState.status === 'completed' && t("extracted")}
                          {processingState.status === 'error' && t("extractionFailed")}
                        </span>

                        {/* Analysis Status */}
                        {processingState.analysisStatus && (
                          <>
                            <span className="text-gray-400">•</span>
                            <span className={`flex items-center ${processingState.analysisStatus === 'analyzing' ? 'text-blue-600' :
                              processingState.analysisStatus === 'analyzed' ? 'text-green-600' :
                                processingState.analysisStatus === 'analysis_error' ? 'text-red-600' : ''
                              }`}>
                              {processingState.analysisStatus === 'analyzing' && (
                                <>
                                  <Brain className="w-3 h-3 mr-1 animate-pulse" />
                                  {t("analyzing")}
                                </>
                              )}
                              {processingState.analysisStatus === 'analyzed' && (
                                <>
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  {t("analyzed")}
                                </>
                              )}
                              {processingState.analysisStatus === 'analysis_error' && (
                                <>
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  {t("analysisFailed")}
                                </>
                              )}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                    {processingState && processingState.status !== 'error' && processingState.progress < 100 && (
                      <Progress value={processingState.progress} className="w-full h-1 mt-1" />
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFile(file)}
                  disabled={disabled}
                  className="text-red-600 hover:text-red-800 hover:bg-red-50"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
